// File: src/pages/admin/AdminChats.jsx
// ReyZa Admin Conversations - Fixed Card Layout

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import io from 'socket.io-client';
import { useUser } from '../../contexts/UserContext';
import { chatAPI } from '../../services/api';
import './css/AdminChats.css';

const SOCKET_SERVER = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

const AdminChats = () => {
  const [conversations, setConversations] = useState([]);
  const [socket, setSocket] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/admin');
      return;
    }

    const token = localStorage.getItem('token');
    const newSocket = io(SOCKET_SERVER, { 
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 3
    });

    newSocket.on('new-message', () => fetchConversations());
    newSocket.on('admin-notification', () => fetchConversations());
    newSocket.on('convo-closed', () => fetchConversations());
    newSocket.on('convo-reopened', () => fetchConversations());

    setSocket(newSocket);
    fetchConversations();

    const interval = setInterval(fetchConversations, 30000);

    return () => {
      newSocket.close();
      clearInterval(interval);
    };
  }, [user, navigate]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await chatAPI.getActiveConversations();
      setConversations(data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const claimConvo = async (convoId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setActionLoading(prev => ({ ...prev, [convoId]: 'claiming' }));
    
    try {
      await chatAPI.claimConversation(convoId);
      await fetchConversations();
      setTimeout(() => navigate(`/admin/chats/${convoId}`), 400);
    } catch (err) {
      console.error('Failed to claim:', err);
      if (err.response?.status === 409) {
        alert('Cette conversation vient d etre prise par un autre admin.');
      } else {
        alert('Echec de prise en charge. Veuillez reessayer.');
      }
      await fetchConversations();
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[convoId];
        return newState;
      });
    }
  };

  const closeConvo = async (convoId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Confirmer la fermeture de cette conversation ?')) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [convoId]: 'closing' }));
    
    try {
      await chatAPI.closeConversation(convoId);
      await fetchConversations();
    } catch (err) {
      console.error('Failed to close:', err);
      alert('Echec de fermeture de la conversation. Veuillez reessayer.');
      await fetchConversations();
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[convoId];
        return newState;
      });
    }
  };

  const filteredConvos = useMemo(() => {
    let filtered = [...conversations];
    
    switch (filter) {
      case 'unclaimed':
        filtered = filtered.filter(c => !c.admin_id);
        break;
      case 'claimed':
        filtered = filtered.filter(c => c.admin_id === user.id);
        break;
      case 'unread':
        filtered = filtered.filter(c => c.unreadCount > 0);
        break;
      default:
        break;
    }
    
    filtered.sort((a, b) => {
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
    
    return filtered;
  }, [conversations, filter, user.id]);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'A l instant';
    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffMins < 1440) return `il y a ${Math.floor(diffMins / 60)} h`;
    if (diffMs < 604800000) return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
  }, []);

  const getUserDisplay = useCallback((convo) => {
    if (convo.user_email) {
      return `${convo.firstName || ''} ${convo.lastName || ''}`.trim() || convo.user_email;
    }
    return 'Invite';
  }, []);

  const getConvoStatus = useCallback((convo) => {
    if (convo.unreadCount > 0) return 'urgent';
    if (convo.admin_id === user.id) return 'mine';
    if (convo.admin_id) return 'claimed';
    return 'unclaimed';
  }, [user.id]);

  const getFilterLabel = (key) => {
    switch (key) {
      case 'all':
        return 'Toutes';
      case 'unread':
        return 'Non lues';
      case 'unclaimed':
        return 'Non attribuees';
      case 'claimed':
        return 'Mes chats';
      default:
        return key;
    }
  };

  const unreadCount = conversations.filter(c => c.unreadCount > 0).length;

  if (loading) {
    return (
      <div className="admin-chats-container">
        <div className="loading-container">
          <div className="loading-spinner" aria-label="Chargement des conversations"></div>
          <p className="loading-text">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-chats-container">
      <header className="admin-chats-header">
        <div className="header-content">
          <div className="header-main">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <svg className="header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-2.4-.322l-3.6 1.8V16.5A8 8 0 1121 12z" />
                </svg>
              </div>
              <div className="header-text">
                <h1 className="header-title">Conversations actives</h1>
                <p className="header-subtitle">
                  {conversations.length} total - {unreadCount} non lus
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <span className="urgent-badge" role="status" aria-live="polite">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" 
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" 
                    clipRule="evenodd" />
                </svg>
                {unreadCount} message{unreadCount > 1 ? 's' : ''} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          <div className="chat-filters" role="group" aria-label="Filtres de conversation">
            {[
              { key: 'all', label: getFilterLabel('all'), count: conversations.length },
              { key: 'unread', label: getFilterLabel('unread'), count: unreadCount },
              { key: 'unclaimed', label: getFilterLabel('unclaimed'), count: conversations.filter(c => !c.admin_id).length },
              { key: 'claimed', label: getFilterLabel('claimed'), count: conversations.filter(c => c.admin_id === user.id).length }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                className={`filter-button ${filter === key ? 'active' : ''}`}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
              >
                {label} ({count})
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="admin-chats-content">
        {filteredConvos.length === 0 ? (
          <div className="empty-state">
            <h2>Aucune conversation trouvee</h2>
            <p>Filtre : {getFilterLabel(filter)}</p>
          </div>
        ) : (
          <div className="convo-list">
            {filteredConvos.map((convo) => (
              <div 
                key={convo.id} 
                className={`convo-item status-${getConvoStatus(convo)}`}
              >
                <Link to={`/admin/chats/${convo.id}`} className="convo-link">
                  <div className="convo-main">
                    <div className="convo-header">
                      <h3 className="convo-title">
                        {convo.subject || 'Assistance generale'}
                      </h3>
                      {convo.unreadCount > 0 && (
                        <span className="unread-badge urgent">
                          {convo.unreadCount}
                        </span>
                      )}
                    </div>
                    
                    <div className="convo-meta">
                      <span className="meta-item">
                        <svg className="meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {getUserDisplay(convo)}
                      </span>
                      <span className="meta-item">
                        <svg className="meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatDate(convo.updated_at)}
                      </span>
                    </div>
                    
                    <div className="convo-stats">
                      <span className="stat-item">
                        <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-2.4-.322l-3.6 1.8V16.5A8 8 0 1121 12z" />
                        </svg>
                        {convo.totalMessages || 0} messages
                      </span>
                      {convo.admin_id && (
                        <span className={`claim-status ${convo.admin_id === user.id ? 'mine' : 'other'}`}>
                          <svg className="status-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" 
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                              clipRule="evenodd" />
                          </svg>
                          {convo.admin_id === user.id ? 'Assigne a vous' : `Assigne a ${convo.adminFirstName || 'Admin'}`}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
                
                <div className="convo-actions">
                  {!convo.admin_id ? (
                    <button 
                      className="action-button claim"
                      onClick={(e) => claimConvo(convo.id, e)}
                      disabled={actionLoading[convo.id]}
                      aria-label="Assigner la conversation"
                    >
                      {actionLoading[convo.id] ? (
                        <span className="button-spinner"></span>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Assigner
                        </>
                      )}
                    </button>
                  ) : convo.admin_id === user.id ? (
                    <button 
                      className="action-button close"
                      onClick={(e) => closeConvo(convo.id, e)}
                      disabled={actionLoading[convo.id]}
                      aria-label="Fermer la conversation"
                    >
                      {actionLoading[convo.id] ? (
                        <span className="button-spinner"></span>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                              d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Fermer
                        </>
                      )}
                    </button>
                  ) : (
                    <button className="action-button disabled" disabled>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Attribuee
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminChats;
